import { useEffect, useRef, useState } from "react";
import PetInformation from "../PetInformation";

type UltrasoundFormState = {
  uterusStatus: string;
  pregnancyStatus: string;
  gsDiameter: string;
  crl: string;
  biparietalDiameter: string;
  viabilityHeartbeat: string;
  fetalHeartRate: string;
  fetalMovement: string;
  notes: string;
  gestationalAge: string;
  viability: string;
  recommendationRescan: boolean;
  doctorName: string;
  examDate: string;
};

const USGPregnancyReport = () => {
  const [formData, setFormData] = useState<UltrasoundFormState>({
    uterusStatus: "",
    pregnancyStatus: "",
    gsDiameter: "",
    crl: "",
    biparietalDiameter: "",
    viabilityHeartbeat: "",
    fetalHeartRate: "",
    fetalMovement: "",
    notes: "",
    gestationalAge: "",
    viability: "",
    recommendationRescan: false,
    doctorName: "",
    examDate: "",
  });

  const notesRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (notesRef.current) {
      notesRef.current.style.height = "auto";
      notesRef.current.style.height = `${notesRef.current.scrollHeight}px`;
    }
  }, [formData.notes]);

  const setValue = (key: keyof UltrasoundFormState, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const renderRadioGroup = (
    name: string,
    key: keyof UltrasoundFormState,
    options: Array<{ label: string; value: string }>,
  ) => {
    return (
      <div className="flex flex-wrap gap-4 text-xs">
        {options.map((option) => (
          <label key={option.value} className="flex items-center gap-1 cursor-pointer">
            <input
              type="radio"
              name={name}
              checked={formData[key] === option.value}
              onChange={() => setValue(key, option.value)}
              className="accent-black"
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
    );
  };

  return (
    <div className="w-full">
      <PetInformation />

      <h3 className="text-sm font-semibold mb-2">Ultrasonographic Findings</h3>
      <table className="w-full border border-gray-300 border-collapse text-xs">
        <thead>
          <tr className="bg-gray-100 print:bg-white">
            <th className="border border-gray-300 p-2 text-left w-[34%]">Parameter</th>
            <th className="border border-gray-300 p-2 text-left">Findings</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border border-gray-300 p-2 font-semibold">Uterus Status</td>
            <td className="border border-gray-300 p-2">
              {renderRadioGroup("uterusStatus", "uterusStatus", [
                { label: "Not Visually Enlarged", value: "not_visually_enlarged" },
                { label: "Enlarged", value: "enlarged" },
                { label: "Fluid-filled", value: "fluid_filled" },
              ])}
            </td>
          </tr>
          <tr>
            <td className="border border-gray-300 p-2 font-semibold">Pregnancy Status</td>
            <td className="border border-gray-300 p-2">
              {renderRadioGroup("pregnancyStatus", "pregnancyStatus", [
                { label: "Positive", value: "positive" },
                { label: "Negative", value: "negative" },
                { label: "Inconclusive", value: "inconclusive" },
              ])}
            </td>
          </tr>
          <tr>
            <td className="border border-gray-300 p-2 font-semibold">Gestational Sac (GS) Diameter</td>
            <td className="border border-gray-300 p-2">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={formData.gsDiameter}
                  onChange={(e) => setValue("gsDiameter", e.target.value)}
                  className="border p-1 w-[200px]"
                />
                <span>cm</span>
              </div>
            </td>
          </tr>
          <tr>
            <td className="border border-gray-300 p-2 font-semibold">Crown-Rump Length (CRL)</td>
            <td className="border border-gray-300 p-2">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={formData.crl}
                  onChange={(e) => setValue("crl", e.target.value)}
                  className="border p-1 w-[200px]"
                />
                <span>cm</span>
              </div>
            </td>
          </tr>
          <tr>
            <td className="border border-gray-300 p-2 font-semibold">Biparietal Diameter</td>
            <td className="border border-gray-300 p-2">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={formData.biparietalDiameter}
                  onChange={(e) => setValue("biparietalDiameter", e.target.value)}
                  className="border p-1 w-[200px]"
                />
                <span>cm</span>
              </div>
            </td>
          </tr>
          <tr>
            <td className="border border-gray-300 p-2 font-semibold">Heartbeat</td>
            <td className="border border-gray-300 p-2">
              {renderRadioGroup("viabilityHeartbeat", "viabilityHeartbeat", [
                { label: "Detected", value: "detected" },
                { label: "Not Detected", value: "not_detected" },
              ])}
            </td>
          </tr>
          <tr>
            <td className="border border-gray-300 p-2 font-semibold">Fetal Heart Rate (FHR)</td>
            <td className="border border-gray-300 p-2">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={formData.fetalHeartRate}
                  onChange={(e) => setValue("fetalHeartRate", e.target.value)}
                  className="border p-1 w-[200px]"
                />
                <span>BPM</span>
              </div>
            </td>
          </tr>
          <tr>
            <td className="border border-gray-300 p-2 font-semibold">Fetal Movement</td>
            <td className="border border-gray-300 p-2">
              {renderRadioGroup("fetalMovement", "fetalMovement", [
                { label: "Present", value: "present" },
                { label: "Absent", value: "absent" },
              ])}
            </td>
          </tr>
        </tbody>
      </table>

      <div className="mt-3">
        <p className="text-sm font-semibold mb-2">Notes / Observations</p>
        <textarea
          ref={notesRef}
          value={formData.notes}
          onChange={(e) => setValue("notes", e.target.value)}
          className="w-full border p-2 text-xs resize-none print:hidden"
          placeholder="Add observations..."
        />
        <div className="hidden print:block w-full p-2 text-xs whitespace-pre-wrap border border-gray-300">{formData.notes}</div>
      </div>

      <div className="mt-3">
        <p className="text-sm font-semibold mb-2">Interpretation / Impression</p>
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xs font-semibold w-[140px]">Gestational Age</p>
            <input
              type="text"
              value={formData.gestationalAge}
              onChange={(e) => setValue("gestationalAge", e.target.value)}
              className="border p-1 w-[240px] text-xs"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xs font-semibold w-[140px]">Viability</p>
            {renderRadioGroup("viability", "viability", [
              { label: "Viable Fetuses", value: "viable_fetuses" },
              { label: "Non-viable / Resorption", value: "non_viable_resorption" },
            ])}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xs font-semibold w-[140px]">Recommendation</p>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={formData.recommendationRescan}
                onChange={(e) => setValue("recommendationRescan", e.target.checked)}
                className="accent-black"
              />
              <span>Re-scan in 7-10 days</span>
            </label>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <p className="text-sm font-semibold mb-2">Examiner Details</p>
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xs font-semibold w-[140px]">Doctor Name</p>
            <input
              type="text"
              value={formData.doctorName}
              onChange={(e) => setValue("doctorName", e.target.value)}
              className="border p-1 w-[240px] text-xs"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xs font-semibold w-[140px]">Date</p>
            <input
              type="date"
              value={formData.examDate}
              onChange={(e) => setValue("examDate", e.target.value)}
              className="border p-1 w-[240px] text-xs print:hidden"
            />
            <span className="hidden print:inline text-xs">{formData.examDate}</span>
          </div>
        </div>
      </div>

      <style>
        {`
          @media print {
            input, textarea {
              border: none !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default USGPregnancyReport;
